import { useIntl } from 'react-intl';

import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingShimmer } from '~/eds/src/components/LoadingShimmer/LoadingShimmer';
import { Text } from '~/eds/src/components/Text/Text';
import { GuidelineReadOnly } from '~/features/playbooks/details/GuidelineReadOnly';
import { api } from '~/redux/api/createApi';
import { GuidelineWithId } from '~/redux/api/types/playbooks/guidelines';
import { copyToClipboard } from '~/utils/helper.utils';

import { Delete } from '../delete/Delete';

type Props = {
  hasEditPermission?: boolean;
  guidelineId: string;
  onEdit: (guideline: GuidelineWithId) => void;
};

export const Details = ({ hasEditPermission, guidelineId, onEdit }: Props) => {
  const { data: guidelineResponse, isFetching } =
    api.endpoints.getGuideline.useQuery(guidelineId);
  const intl = useIntl();

  const guideline = guidelineResponse?.data?.attributes;

  return (
    <Layout flex="1" direction="column" w="100%" overflowY="scroll">
      {isFetching ? (
        <Layout preset="subsections" flex="1">
          <LoadingShimmer height="header.height" />
          <LoadingShimmer height="header.height" />
          <LoadingShimmer height="120px" />
          <LoadingShimmer height="340px" />
        </Layout>
      ) : (
        <>
          <Layout
            borderBottom="border.divider"
            minH="header.height"
            justify="space-between"
            px={4}
            align="center"
            bg="white"
            flex="none"
          >
            {}
            <Text variant="body-bold" fontSize="16">
              {guideline?.name}
            </Text>
            <Layout>
              <IconButton
                icon="clipboard"
                onClick={() => {
                  copyToClipboard(
                    guideline?.guideline,
                    intl.formatMessage({
                      id: 'guidelines.copySuccess',
                      defaultMessage: 'Guideline copied to clipboard',
                    }),
                  );
                }}
              />
              <IconButton
                disabled={!hasEditPermission}
                icon="edit"
                onClick={() => {
                  if (guideline) {
                    onEdit(guideline);
                  }
                }}
              />
              <Delete
                disabled={!hasEditPermission}
                guidelineId={guidelineId}
                guidelineName={guideline?.name ?? ''}
              />
            </Layout>
          </Layout>
          <Layout
            direction="column"
            py={4}
            spacing={4}
            maxW="100%"
            overflowY="scroll"
          >
            <GuidelineReadOnly
              name={guideline?.name ?? ''}
              guideline={guideline?.guideline ?? ''}
              sample={guideline?.sample ?? ''}
              variations={guideline?.variations}
              suggestRedlines={guideline?.suggest_redlines ?? false}
              mode="inline"
            />
          </Layout>
        </>
      )}
    </Layout>
  );
};
